import { Edit, Email as EmailIcon, PictureAsPdf } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Associations, Sending } from 'src/modules/invoice/models/invoiceTemplate';
import { BlankettPicker } from 'src/modules/sending/components/blankettPicker';
import { SendingEmailMock } from 'src/modules/sending/components/sendingEmailMock';
import { SendingInvoiceMock } from 'src/modules/sending/components/sendingInvoiceMock';
import { PreviewPane, TextEditorWithPreview } from 'src/modules/sending/components/textEditorWithPreview';
import { accountTypeToBlankettOptions, allBlankettOptions } from 'src/modules/sending/functions/blankettOptions';
import ControlledTextEditor from 'src/shared/components/form/controlledTextEditor';
import { useGlobals } from 'src/shared/contexts/appContext';
import { getPermission } from 'src/shared/functions/getSysProductPermissions';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { InvoiceDataFieldsEnum } from 'src/shared/models/enums/dataFields.enum';
import { InvoiceSendingTypeIntegerEnum } from 'src/shared/models/enums/invoiceSendingTypeInteger.enum';
import { PermissionEnum } from 'src/shared/models/enums/permissionEnums';
import { getInvoiceGroupById } from 'src/shared/services/invoiceGroupService';
import { sendInvoiceTemplateTestEmail } from 'src/shared/services/invoiceService';
import { useInvoiceTemplateType } from '../hooks/useInvoiceTemplateType';
import { CreateInvoiceTemplatePreviewRequest } from '../models/createInvoiceTemplatePreviewRequest';
import { PreviewInvoiceTemplateDownloader } from './previewInvoiceTemplateDownloader';

// Konfiguration för vilken försändelsetyp som visar fakturablocket.
const sendingConfigByType: Record<number, { titleKey: string; showInvoice: boolean }> = {
  [InvoiceSendingTypeIntegerEnum.Email]: {
    titleKey: 'invoice:adminInvoiceTemplatePage.emailSending',
    showInvoice: false,
  },
  [InvoiceSendingTypeIntegerEnum.Main]: {
    titleKey: 'invoice:adminInvoiceTemplatePage.mainSending',
    showInvoice: true,
  },
  [InvoiceSendingTypeIntegerEnum.Reminder1]: {
    titleKey: 'invoice:adminInvoiceTemplatePage.reminderOne',
    showInvoice: true,
  },
  [InvoiceSendingTypeIntegerEnum.Reminder2]: {
    titleKey: 'invoice:adminInvoiceTemplatePage.reminderTwo',
    showInvoice: true,
  },
};

export const getSendingConfig = (sendingType: number) =>
  sendingConfigByType[sendingType] ?? {
    titleKey: 'invoice:adminInvoiceTemplatePage.emailSending',
    showInvoice: false,
  };

export const isSendingNotApproved = (sendingItem: Sending) => sendingItem.notReady || sendingItem.textApproved === false;

type TabKey = 'email' | 'invoice' | 'blankett' | 'preview';

type RenderInvoiceTemplateSendingProps = {
  loading: boolean;
  setLoading: (pdfLoader: boolean) => void;
  sendingItem: Sending;
  index: number;
};

// Samma upplägg som utskicksbeställningens Texter-steg: en underflik per innehållstyp och en
// förhandsvisning bredvid som följer det man skriver. Blanketten hör till hela mallen men väljs
// under huvudutskicket, precis som i beställningen, och påminnelserna ärver den.
export const RenderInvoiceTemplateSending = (props: RenderInvoiceTemplateSendingProps) => {
  const { loading, setLoading, sendingItem, index } = props;
  const { watch, setValue } = useFormContext();
  const { isSwish } = useInvoiceTemplateType();
  const { currentUser } = useGlobals();
  const isMounted = useIsMounted();

  const [edit, setEdit] = useState<boolean>(false);
  const [accountType, setAccountType] = useState<number | null>(null);
  const [accountNr, setAccountNr] = useState<string>('');

  const sendingConfig = getSendingConfig(sendingItem.sendingType);
  const sendingTitle = t(sendingConfig.titleKey);
  // Swishmallar skriver ingen avi, så blankettfliken hör inte hemma där.
  const isMainSending = sendingItem.sendingType === InvoiceSendingTypeIntegerEnum.Main && !isSwish;

  const [tab, setTab] = useState<TabKey>('email');

  const dataToPdf: CreateInvoiceTemplatePreviewRequest = {
    templateSendingsId: sendingItem.templateSendings_id,
  };

  const buildFieldName = (field: 'emailHeading' | 'emailText' | 'defaultHeading' | 'defaultText') => `sendings[${index}].${field}`;

  const ownerId = watch('owner_id') as number | undefined;
  const formTemplate = (watch('formTemplate') as string) || '';
  const useLocalAssLogo = !!watch('useLocalAssLogo');
  const emailHeading = (watch(buildFieldName('emailHeading')) as string) || '';
  const emailText = (watch(buildFieldName('emailText')) as string) || '';
  const invoiceHeading = (watch(buildFieldName('defaultHeading')) as string) || '';
  const invoiceText = (watch(buildFieldName('defaultText')) as string) || '';

  // Kontotyp och kontonummer hämtas från fakturagruppen: kontotypen styr vilka blanketter kunden
  // får välja, kontonumret visas på inbetalningskortet i förhandsvisningen.
  useEffect(() => {
    if (!ownerId) return;

    const fetchInvoiceGroup = async () => {
      try {
        const result = await getInvoiceGroupById(ownerId);
        if (!isMounted()) return;
        setAccountType(result.data.accountType ?? null);
        setAccountNr(result.data.accountNr ?? '');
      } catch (err) {
        if (isMounted()) ConsoleHelper.log(err);
      }
    };

    fetchInvoiceGroup();
  }, [ownerId, isMounted]);

  // SuperAdmin ser samtliga blanketter, inklusive medlemskort och autogiro. Kunden ser bara de som
  // passar fakturagruppens kontotyp, precis som i utskicksbeställningen.
  const canSeeAllBlanketter = getPermission(currentUser, PermissionEnum.InvoiceCreateForCustomer);
  const blankettOptions = canSeeAllBlanketter ? allBlankettOptions : accountTypeToBlankettOptions(accountType);

  // Ett val utanför kundens lista är satt av oss och ska aldrig skrivas om av att fliken öppnas.
  const blankettChosenByFs = !!formTemplate && !blankettOptions.some((o) => o.code === formTemplate);

  // Testutskick: skickar ett riktigt mejl med e-postrubriken och texten som de ser ut just nu, och
  // en exempelfaktura bifogad som PDF. Samma väg och avsändare som utskicksbeställningen använder,
  // och ingenting sparas på mallen, så texterna behöver inte vara sparade för att kunna provas.
  const associations: Associations[] = watch('associations') || [];
  const [testOpen, setTestOpen] = useState(false);
  const [testEmail, setTestEmail] = useState(currentUser?.email ?? '');
  const [testSending, setTestSending] = useState(false);
  const [testAssociationId, setTestAssociationId] = useState<number | null>(null);

  // Loggan byts per förening, så när mallen använder föreningens logga och urvalet omfattar flera
  // föreningar får man välja vilken av dem testutskicket ska se ut som.
  const chooseAssociation = useLocalAssLogo && associations.length > 1;

  const openTestModal = () => {
    setTestAssociationId(associations[0]?.id ?? null);
    setTestOpen(true);
  };

  const sendTestEmail = async () => {
    if (!ownerId || ownerId <= 0 || !testEmail.trim()) return;

    setTestSending(true);
    try {
      await sendInvoiceTemplateTestEmail({
        toEmail: testEmail.trim(),
        ownerId,
        formTemplate: formTemplate || 'bgocr_2024',
        emailHeading,
        emailText,
        invoiceHeading,
        invoiceText,
        localAssId: useLocalAssLogo ? testAssociationId ?? associations[0]?.id : undefined,
      });
      if (!isMounted()) return;
      toast.success(t('invoice:adminInvoiceTemplatePage.sendTestEmailSuccess', { email: testEmail.trim() }) as string);
      setTestOpen(false);
    } catch (err) {
      ConsoleHelper.log(err);
      if (isMounted()) toast.error(t('invoice:adminInvoiceTemplatePage.sendTestEmailError') as string);
    } finally {
      if (isMounted()) setTestSending(false);
    }
  };

  const editButton = (
    <Button size='medium' variant='outlined' endIcon={<Edit fontSize='small' />} disabled={edit || loading} onClick={() => setEdit(true)}>
      {t('common:button.edit')}
    </Button>
  );

  const invoiceMock = (highlight: 'text' | 'slip' | null) => (
    <SendingInvoiceMock
      highlight={highlight}
      formTemplate={formTemplate}
      invoiceHeading={invoiceHeading}
      invoiceText={invoiceText}
      accountNr={accountNr}
      senderName={currentUser?.customerName}
      useLocalAssLogo={useLocalAssLogo}
    />
  );

  const tabOrder: TabKey[] = ['email', sendingConfig.showInvoice ? 'invoice' : null, isMainSending ? 'blankett' : null, 'preview'].filter(
    Boolean,
  ) as TabKey[];

  const tabLabels: Record<TabKey, string> = {
    email: t('invoice:adminInvoiceTemplatePage.emailContent'),
    invoice: t('invoice:adminInvoiceTemplatePage.invoiceContent'),
    blankett: t('invoice:adminInvoiceTemplatePage.formTemplateTab'),
    preview: t('invoice:createMemberInvoice.preview'),
  };

  return (
    <Box>
      {isSendingNotApproved(sendingItem) && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {t('invoice:adminInvoiceTemplatePage.invoiceTemplateNotApproved')}
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, value) => setTab(value as TabKey)} sx={{ mb: 2 }}>
        {tabOrder.map((key) => (
          <Tab key={key} value={key} label={tabLabels[key]} />
        ))}
      </Tabs>

      {tab === 'email' && (
        <TextEditorWithPreview
          label={`${sendingTitle} - ${tabLabels.email}`}
          helper={t('invoice:adminInvoiceTemplatePage.emailContentInfo')}
          headingName={buildFieldName('emailHeading')}
          headingLabel={t('invoice:createMemberInvoice.heading')}
          headingDisabled={!edit}
          action={editButton}
          editor={<ControlledTextEditor adminInvoiceEmailSection disabled={!edit} rows={40} name={buildFieldName('emailText')} />}
          preview={<SendingEmailMock senderName={currentUser?.customerName} heading={emailHeading} html={emailText} />}
        />
      )}

      {tab === 'invoice' && (
        <TextEditorWithPreview
          label={`${sendingTitle} - ${tabLabels.invoice}`}
          headingName={buildFieldName('defaultHeading')}
          headingLabel={t('invoice:createMemberInvoice.heading')}
          headingDisabled={!edit}
          action={editButton}
          editor={
            <ControlledTextEditor
              adminInvoice
              disabled={!edit}
              rows={40}
              name={buildFieldName('defaultText')}
              // Bara de fält som textgeneratorn faktiskt byter ut när fakturan skapas. Övriga
              // datafält i systemet hör till mejlmotorn och hade blivit stående text här.
              allowedDataFields={[InvoiceDataFieldsEnum.Name, InvoiceDataFieldsEnum.MemberNr, InvoiceDataFieldsEnum.LocalAss]}
            />
          }
          preview={invoiceMock('text')}
        />
      )}

      {tab === 'blankett' && (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'stretch', mb: 3, minHeight: { md: 720 } }}>
          <Box sx={{ flex: { md: '0 0 57%' }, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
              <Typography variant='h6' sx={{ fontWeight: 700 }}>
                {t('invoice:adminInvoiceTemplatePage.formTemplateTab')}
              </Typography>
              {editButton}
            </Box>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
              {t('invoice:adminInvoiceTemplatePage.formTemplateHelper')}
            </Typography>

            {blankettChosenByFs && (
              <Alert severity='info' sx={{ mb: 2 }}>
                {t('invoice:adminInvoiceTemplatePage.formTemplateChosenByFs')}
              </Alert>
            )}

            <BlankettPicker options={blankettOptions} value={formTemplate} onChange={(code) => setValue('formTemplate', code)} locked={!edit} />
          </Box>

          <PreviewPane basis='43%'>{invoiceMock('slip')}</PreviewPane>
        </Box>
      )}

      {tab === 'preview' && (
        // Två kolumner som i utskicksbeställningen: mejlet till vänster och fakturan till höger.
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'stretch', mb: 3, minHeight: { md: 720 } }}>
          <Box sx={{ flex: 1, minWidth: 0, maxWidth: { md: '50%' }, pr: { md: 2 }, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 0.25 }}>
              <Typography variant='h6' sx={{ fontWeight: 700 }}>
                {t('invoice:adminInvoiceTemplatePage.previewEmailTitle')}
              </Typography>
              <Button
                variant='outlined'
                startIcon={<EmailIcon />}
                disabled={!ownerId}
                onClick={openTestModal}
                sx={{ textTransform: 'none', flexShrink: 0 }}
              >
                {t('invoice:adminInvoiceTemplatePage.sendTestEmail')}
              </Button>
            </Box>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
              {t('invoice:adminInvoiceTemplatePage.previewEmailHelper')} {t('invoice:adminInvoiceTemplatePage.sendTestEmailHelper')}
            </Typography>
            {/* Grå yta så den vita previewen framhävs. */}
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', bgcolor: 'grey.100', borderRadius: 1, p: 2 }}>
              <SendingEmailMock senderName={currentUser?.customerName} heading={emailHeading} html={emailText} />
            </Box>
          </Box>

          {sendingConfig.showInvoice && (
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                maxWidth: { md: '50%' },
                pl: { md: 2 },
                // Longhand så färgen inte återställs av borderLeft-shorthand inne i media-queryn.
                borderLeftWidth: { md: '1px' },
                borderLeftStyle: 'solid',
                borderLeftColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 0.25 }}>
                <Typography variant='h6' sx={{ fontWeight: 700 }}>
                  {t('invoice:adminInvoiceTemplatePage.previewInvoiceTitle')}
                </Typography>
                <Button
                  variant='outlined'
                  startIcon={<PictureAsPdf />}
                  disabled={loading}
                  onClick={() => PreviewInvoiceTemplateDownloader({ dataToPdf, loadingCallback: setLoading })}
                  sx={{ textTransform: 'none', flexShrink: 0 }}
                >
                  {t('invoice:createMemberInvoice.previewInvoice')}
                </Button>
              </Box>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
                {t('invoice:adminInvoiceTemplatePage.previewInvoiceHelper')}
              </Typography>
              <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', bgcolor: 'grey.100', borderRadius: 1, p: 2 }}>
                <SendingInvoiceMock
                  formTemplate={formTemplate}
                  invoiceHeading={invoiceHeading}
                  invoiceText={invoiceText}
                  accountNr={accountNr}
                  senderName={currentUser?.customerName}
                  width={300}
                  useLocalAssLogo={useLocalAssLogo}
                />
              </Box>
            </Box>
          )}
        </Box>
      )}

      <Dialog open={testOpen} onClose={() => setTestOpen(false)} maxWidth='xs' fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{t('invoice:adminInvoiceTemplatePage.sendTestEmail')}</DialogTitle>
        <DialogContent>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            {t('invoice:adminInvoiceTemplatePage.sendTestEmailInfo')}
          </Typography>

          {chooseAssociation && (
            <TextField
              select
              fullWidth
              label={t('invoice:adminInvoiceTemplatePage.sendTestEmailAssociation')}
              value={testAssociationId ?? ''}
              onChange={(e) => setTestAssociationId(Number(e.target.value) || null)}
              helperText={t('invoice:adminInvoiceTemplatePage.sendTestEmailAssociationHelper')}
              sx={{ mb: 2 }}
            >
              {associations.map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  {a.name}
                </MenuItem>
              ))}
            </TextField>
          )}

          {useLocalAssLogo && associations.length === 0 && (
            <Alert severity='info' sx={{ mb: 2 }}>
              {t('invoice:adminInvoiceTemplatePage.sendTestEmailAllAssociations')}
            </Alert>
          )}

          <TextField
            autoFocus
            fullWidth
            type='email'
            label={t('invoice:adminInvoiceTemplatePage.sendTestEmailRecipient')}
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && testEmail.trim() && !testSending) sendTestEmail();
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant='text' onClick={() => setTestOpen(false)} disabled={testSending}>
            {t('common:button.cancel')}
          </Button>
          <Button
            variant='contained'
            disableElevation
            startIcon={testSending ? <CircularProgress size={16} /> : <EmailIcon />}
            disabled={testSending || !testEmail.trim() || !ownerId}
            onClick={sendTestEmail}
          >
            {t('common:button.send')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
